interface AddictionRecoveryGuardrailConfig {
  therapeuticFrameworks: {
    shameFree: boolean;
    strengthsBased: boolean;
    traumaInformed: boolean;
    recoveryOriented: boolean;
  };
  languageGuidelines: {
    nonPathologizing: boolean;
    empowermentFocus: boolean;
    processRespect: boolean;
    maleSpecific: boolean;
  };
  recoverySupport: {
    relapseNormalization: boolean;
    progressCelebration: boolean;
    underlyingNeeds: boolean;
    healingFocus: boolean;
  };
  safetyProtocols: {
    crisisIntervention: boolean;
    suicidalityAwareness: boolean;
    resourceProvision: boolean;
    professionalSupport: boolean;
  };
}

export const ADDICTION_RECOVERY_GUARDRAILS: AddictionRecoveryGuardrailConfig = {
  therapeuticFrameworks: {
    shameFree: true,
    strengthsBased: true,
    traumaInformed: true,
    recoveryOriented: true
  },
  languageGuidelines: {
    nonPathologizing: true,
    empowermentFocus: true,
    processRespect: true,
    maleSpecific: true
  },
  recoverySupport: {
    relapseNormalization: true,
    progressCelebration: true,
    underlyingNeeds: true,
    healingFocus: true
  },
  safetyProtocols: {
    crisisIntervention: true,
    suicidalityAwareness: true,
    resourceProvision: true,
    professionalSupport: true
  }
};

export const validateAddictionRecoveryContent = (content: string): { isValid: boolean; violations: string[]; corrections: string[] } => {
  const violations: string[] = [];
  const corrections: string[] = [];

  // Check for shame-based language
  const shameWords = ['addict', 'perverted', 'sick', 'disgusting', 'failure', 'pathetic', 'weak'];
  const lowerContent = content.toLowerCase();
  
  shameWords.forEach(word => {
    if (lowerContent.includes(word)) {
      violations.push(`Shame-based language detected: "${word}"`);
      corrections.push('Use person-first, recovery-oriented language instead');
    }
  });

  // Ensure recovery-oriented approach
  if (!lowerContent.includes('recovery') && !lowerContent.includes('healing') && !lowerContent.includes('growth')) {
    violations.push('Missing recovery-focused language');
    corrections.push('Include language that emphasizes recovery, healing, and personal growth');
  }

  // Check for underlying needs recognition
  const needsWords = ['need', 'emotion', 'feeling', 'stress', 'trauma', 'connection', 'intimacy'];
  const hasNeeds = needsWords.some(word => lowerContent.includes(word));
  
  if (lowerContent.includes('addiction') || lowerContent.includes('compulsive')) {
    if (!hasNeeds) {
      violations.push('Missing underlying needs recognition');
      corrections.push('Include recognition of underlying emotional needs and trauma');
    }
  }

  // Ensure strength-based approach
  const strengthWords = ['strength', 'courage', 'resilience', 'capable', 'power', 'ability', 'skill'];
  const hasStrength = strengthWords.some(word => lowerContent.includes(word));
  
  if (!hasStrength) {
    violations.push('Missing strength-based language');
    corrections.push('Include language that recognizes inherent strengths and capabilities');
  }

  // Check for relapse normalization
  if (lowerContent.includes('relapse') || lowerContent.includes('slip') || lowerContent.includes('setback')) {
    if (!lowerContent.includes('normal') && !lowerContent.includes('part of') && !lowerContent.includes('learning')) {
      violations.push('Missing relapse normalization');
      corrections.push('Normalize relapses as part of the recovery learning process');
    }
  }

  // Ensure male-specific considerations
  if (lowerContent.includes('men') || lowerContent.includes('male') || lowerContent.includes('masculine')) {
    const maleWords = ['provider', 'protector', 'emotional', 'vulnerability', 'connection'];
    const hasMaleSpecific = maleWords.some(word => lowerContent.includes(word));
    
    if (!hasMaleSpecific) {
      violations.push('Missing male-specific therapeutic considerations');
      corrections.push('Include male-specific themes around emotions, relationships, and identity');
    }
  }

  return {
    isValid: violations.length === 0,
    violations,
    corrections
  };
};

export const createAddictionRecoverySafetyPrompt = (): string => {
  return `
CRITICAL ADDICTION RECOVERY GUIDELINES:
- Use shame-free, person-first language throughout
- Focus on recovery, healing, and growth rather than abstinence alone
- Recognize underlying emotional needs and trauma
- Normalize relapses as learning opportunities in recovery
- Emphasize inherent strengths and resilience
- Include male-specific considerations around emotions and relationships
- Address shame, isolation, and emotional disconnection
- Support healthy coping mechanisms and emotional regulation
- Honor Michele's expertise in addiction and recovery work
- Include crisis intervention awareness for suicidal ideation
- Provide hope and realistic expectations for recovery journey
- Focus on building authentic connections and emotional intimacy
`;
};

export const isAddictionRecoveryContentSafe = (content: string): boolean => {
  const validation = validateAddictionRecoveryContent(content);
  return validation.isValid;
};